import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import * as XLSX from 'xlsx';
import { useSubscription } from '../../context/SubscriptionContext';
import { useNavigate } from 'react-router-dom';

const UnshippedProductsModal = ({ isOpen, onClose, orders, userData }) => {
    const { hasRestriction } = useSubscription();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [summaryData, setSummaryData] = useState([]);
    const [detailedData, setDetailedData] = useState([]);
    const [activeTab, setActiveTab] = useState('summary'); // 'summary' or 'detailed'
    const [searchTerm, setSearchTerm] = useState('');
    
    // Sort configuration
    const [sortConfig, setSortConfig] = useState({ key: 'totalUnshipped', direction: 'desc' });
    const [detailSortConfig, setDetailSortConfig] = useState({ key: 'date', direction: 'asc' });

    useEffect(() => {
        if (!isOpen || !userData?.companyId || !orders) return;

        let isMounted = true;

        const fetchData = async () => {
            setLoading(true);
            try {
                const unshippedProductsMap = new Map();

                for (const order of orders) {
                    if (order.status === 'Tamamlandı') continue;

                    const shipmentsSnapshot = await new Promise((resolve) => {
                        const unsubscribe = onSnapshot(
                            collection(db, 'companies', userData.companyId, 'orders', order.id, 'shipments'),
                            (snapshot) => {
                                resolve(snapshot);
                                unsubscribe();
                            }
                        );
                    });

                    const shipments = shipmentsSnapshot.docs.map(doc => doc.data());
                    const shippedQuantities = {};
                    
                    shipments.forEach(shipment => {
                        (shipment.items || []).forEach(item => {
                            shippedQuantities[item.productId] = (shippedQuantities[item.productId] || 0) + item.quantity;
                        });
                    });

                    (order.lineItems || []).forEach(item => {
                        const shipped = shippedQuantities[item.productId] || 0;
                        const unshipped = item.quantity - shipped;

                        if (unshipped > 0) {
                            const key = item.productId;
                            if (unshippedProductsMap.has(key)) {
                                const existing = unshippedProductsMap.get(key);
                                existing.totalUnshipped += unshipped;
                                existing.totalOrdered += item.quantity;
                                existing.totalShipped += shipped;
                                existing.orderCount += 1;
                                existing.orders.push({
                                    orderId: order.id,
                                    orderNo: order.orderNumber || order.id.substring(0, 6),
                                    customer: order.customerName,
                                    ordered: item.quantity,
                                    shipped: shipped,
                                    remaining: unshipped,
                                    date: order.orderDate
                                });
                            } else {
                                unshippedProductsMap.set(key, {
                                    productId: item.productId,
                                    totalUnshipped: unshipped,
                                    totalOrdered: item.quantity,
                                    totalShipped: shipped,
                                    orderCount: 1,
                                    orders: [{
                                        orderId: order.id,
                                        orderNo: order.orderNumber || order.id.substring(0, 6),
                                        customer: order.customerName,
                                        ordered: item.quantity,
                                        shipped: shipped,
                                        remaining: unshipped,
                                        date: order.orderDate
                                    }]
                                });
                            }
                        }
                    });
                }

                const productsSnapshot = await new Promise((resolve) => {
                    const unsubscribe = onSnapshot(
                        collection(db, 'companies', userData.companyId, 'products'),
                        (snapshot) => {
                            resolve(snapshot);
                            unsubscribe();
                        }
                    );
                });

                const productsMap = new Map();
                const productBarcodesMap = new Map();
                productsSnapshot.docs.forEach(doc => {
                    const product = doc.data();
                    productsMap.set(doc.id, { 
                        name: product.name || 'Bilinmeyen Ürün',
                        imageUrl: product.imageUrl || null
                    });
                    productBarcodesMap.set(doc.id, product.barcode || '-');
                });

                const sData = [];
                const dData = [];

                unshippedProductsMap.forEach((productData, productId) => {
                    const pInfo = productsMap.get(productId) || { name: 'Bilinmeyen Ürün', imageUrl: null };
                    
                    sData.push({
                        productId,
                        barcode: productBarcodesMap.get(productId) || '-',
                        name: pInfo.name,
                        imageUrl: pInfo.imageUrl,
                        totalOrdered: productData.totalOrdered,
                        totalShipped: productData.totalShipped,
                        totalUnshipped: productData.totalUnshipped,
                        orderCount: productData.orderCount,
                        completionRate: ((productData.totalShipped / productData.totalOrdered) * 100).toFixed(1)
                    });

                    productData.orders.forEach(orderDetail => {
                        const orderDate = orderDetail.date?.seconds ? new Date(orderDetail.date.seconds * 1000) : new Date();
                        const daysWaiting = Math.floor((new Date() - orderDate) / (1000 * 60 * 60 * 24));

                        dData.push({
                            productId,
                            barcode: productBarcodesMap.get(productId) || '-',
                            name: pInfo.name,
                            imageUrl: pInfo.imageUrl,
                            orderId: orderDetail.orderId,
                            orderNo: orderDetail.orderNo,
                            customer: orderDetail.customer,
                            ordered: orderDetail.ordered,
                            shipped: orderDetail.shipped,
                            remaining: orderDetail.remaining,
                            date: orderDate,
                            daysWaiting: daysWaiting >= 0 ? daysWaiting : 0
                        });
                    });
                });

                if (isMounted) {
                    setSummaryData(sData);
                    setDetailedData(dData);
                    setLoading(false);
                }

            } catch (error) {
                console.error('Gönderilmeyen ürünler yüklenirken hata:', error);
                if (isMounted) setLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [isOpen, userData, orders]);
    const handleExport = () => {
        if (!hasRestriction('excelExport')) {
            alert('Excel dışa aktarma özelliği sadece ücretli planlarda mevcuttur. Lütfen aboneliğinizi yükseltin.');
            return;
        }

        const summaryExport = summaryData.map(item => ({
            'Stok Kodu': item.barcode,
            'Ürün Adı': item.name,
            'Toplam Sipariş Edilen': item.totalOrdered,
            'Toplam Gönderilen': item.totalShipped,
            'Gönderilmeyen Miktar': item.totalUnshipped,
            'Sipariş Sayısı': item.orderCount,
            'Tamamlanma Oranı (%)': item.completionRate
        }));

        const detailedExport = detailedData.map(item => ({
            'Stok Kodu': item.barcode,
            'Ürün Adı': item.name,
            'Sipariş No': item.orderNo,
            'Müşteri': item.customer,
            'Bekleme Süresi': `${item.daysWaiting} Gün`,
            'Sipariş Edilen': item.ordered,
            'Gönderilen': item.shipped,
            'Kalan': item.remaining
        }));

        const workbook = XLSX.utils.book_new();

        if (summaryExport.length > 0) {
            const summaryWorksheet = XLSX.utils.json_to_sheet(summaryExport);
            XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Özet");
        }

        if (detailedExport.length > 0) {
            const detailedWorksheet = XLSX.utils.json_to_sheet(detailedExport);
            XLSX.utils.book_append_sheet(workbook, detailedWorksheet, "Detay");
        }

        if (summaryExport.length === 0) {
            const emptyData = [{ 'Mesaj': 'Gönderilmeyen ürün bulunmuyor' }];
            const emptyWorksheet = XLSX.utils.json_to_sheet(emptyData);
            XLSX.utils.book_append_sheet(workbook, emptyWorksheet, "Sonuç");
        }

        const fileName = `Gonderilmeyen_Urunler_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '_')}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handleDetailSort = (key) => {
        let direction = 'asc';
        if (detailSortConfig.key === key && detailSortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setDetailSortConfig({ key, direction });
    };

    const sortedSummaryData = useMemo(() => {
        let sortableItems = [...summaryData];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [summaryData, sortConfig]);

    const sortedDetailedData = useMemo(() => {
        let sortableItems = [...detailedData];
        if (detailSortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                if (a[detailSortConfig.key] < b[detailSortConfig.key]) {
                    return detailSortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[detailSortConfig.key] > b[detailSortConfig.key]) {
                    return detailSortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [detailedData, detailSortConfig]);

    const filteredSummary = sortedSummaryData.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        item.barcode.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredDetailed = sortedDetailedData.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        item.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.orderNo.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getSortIcon = (key, currentConfig) => {
        if (currentConfig.key !== key) return '↕️';
        return currentConfig.direction === 'asc' ? '↑' : '↓';
    };

    if (!isOpen) return null;

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <div>
                        <h2 style={styles.title}>📦 Gönderilmeyen Ürünler</h2>
                        <p style={styles.subtitle}>Sipariş edilmiş ancak henüz sevk edilmemiş ürünlerin listesi</p>
                    </div>
                    <button style={styles.closeBtn} onClick={onClose}>×</button>
                </div>

                <div style={styles.controls}>
                    <div style={styles.tabs}>
                        <button 
                            style={{ ...styles.tab, ...(activeTab === 'summary' ? styles.activeTab : {}) }}
                            onClick={() => setActiveTab('summary')}
                        >
                            Ürün Özeti
                        </button>
                        <button 
                            style={{ ...styles.tab, ...(activeTab === 'detailed' ? styles.activeTab : {}) }}
                            onClick={() => setActiveTab('detailed')}
                        >
                            Sipariş Detayları
                        </button>
                    </div>
                    
                    <div style={styles.actions}>
                        <div style={styles.searchContainer}>
                            <span style={styles.searchIcon}>🔍</span>
                            <input 
                                type="text" 
                                placeholder="Ürün, Müşteri veya Sipariş No..." 
                                style={styles.searchInput}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button style={styles.exportBtn} onClick={handleExport}>
                            📥 Excel'e Aktar
                        </button>
                    </div>
                </div>

                <div style={styles.content}>
                    {loading ? (
                        <div style={styles.loadingContainer}>
                            <div style={styles.spinner}></div>
                            <p style={styles.loadingText}>Veriler hesaplanıyor, lütfen bekleyin...</p>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'summary' && (
                                <div style={styles.tableWrapper}>
                                    <table style={styles.table}>
                                        <thead style={styles.thead}>
                                            <tr>
                                                <th style={{...styles.th, cursor: 'pointer'}} onClick={() => handleSort('barcode')}>Stok Kodu {getSortIcon('barcode', sortConfig)}</th>
                                                <th style={{...styles.th, cursor: 'pointer'}} onClick={() => handleSort('name')}>Ürün Adı {getSortIcon('name', sortConfig)}</th>
                                                <th style={{...styles.th, textAlign: 'center', cursor: 'pointer'}} onClick={() => handleSort('orderCount')}>Bekleyen Sipariş {getSortIcon('orderCount', sortConfig)}</th>
                                                <th style={{...styles.th, textAlign: 'right', cursor: 'pointer'}} onClick={() => handleSort('totalOrdered')}>Sipariş Edilen {getSortIcon('totalOrdered', sortConfig)}</th>
                                                <th style={{...styles.th, textAlign: 'right', cursor: 'pointer'}} onClick={() => handleSort('totalShipped')}>Gönderilen {getSortIcon('totalShipped', sortConfig)}</th>
                                                <th style={{...styles.th, textAlign: 'right', cursor: 'pointer'}} onClick={() => handleSort('totalUnshipped')}>Kalan Miktar {getSortIcon('totalUnshipped', sortConfig)}</th>
                                                <th style={{...styles.th, cursor: 'pointer'}} onClick={() => handleSort('completionRate')}>İlerleme {getSortIcon('completionRate', sortConfig)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredSummary.length > 0 ? (
                                                filteredSummary.map((item, idx) => (
                                                    <tr key={idx} style={styles.tr}>
                                                        <td style={styles.td}><span style={styles.badge}>{item.barcode}</span></td>
                                                        <td style={{...styles.td, fontWeight: '600', color: '#1e293b'}}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                {item.imageUrl ? (
                                                                    <img src={item.imageUrl} alt={item.name} style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                                                                ) : (
                                                                    <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', border: '1px solid #e2e8f0' }}>📦</div>
                                                                )}
                                                                <span>{item.name}</span>
                                                            </div>
                                                        </td>
                                                        <td style={{...styles.td, textAlign: 'center'}}>{item.orderCount}</td>
                                                        <td style={{...styles.td, textAlign: 'right', color: '#64748b'}}>{item.totalOrdered}</td>
                                                        <td style={{...styles.td, textAlign: 'right', color: '#10b981'}}>{item.totalShipped}</td>
                                                        <td style={{...styles.td, textAlign: 'right', fontWeight: '700', color: '#ef4444', fontSize: '16px'}}>
                                                            {item.totalUnshipped}
                                                        </td>
                                                        <td style={styles.td}>
                                                            <div style={styles.progressContainer}>
                                                                <div style={{...styles.progressBar, width: `${item.completionRate}%`}}></div>
                                                            </div>
                                                            <div style={styles.progressText}>{item.completionRate}%</div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" style={styles.emptyState}>Gönderilmeyen ürün bulunamadı.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {activeTab === 'detailed' && (
                                <div style={styles.tableWrapper}>
                                    <table style={styles.table}>
                                        <thead style={styles.thead}>
                                            <tr>
                                                <th style={{...styles.th, cursor: 'pointer'}} onClick={() => handleDetailSort('orderNo')}>Sipariş No {getSortIcon('orderNo', detailSortConfig)}</th>
                                                <th style={{...styles.th, cursor: 'pointer'}} onClick={() => handleDetailSort('customer')}>Müşteri {getSortIcon('customer', detailSortConfig)}</th>
                                                <th style={{...styles.th, cursor: 'pointer'}} onClick={() => handleDetailSort('name')}>Ürün {getSortIcon('name', detailSortConfig)}</th>
                                                <th style={{...styles.th, cursor: 'pointer'}} onClick={() => handleDetailSort('daysWaiting')}>Bekleme {getSortIcon('daysWaiting', detailSortConfig)}</th>
                                                <th style={{...styles.th, textAlign: 'right', cursor: 'pointer'}} onClick={() => handleDetailSort('remaining')}>Kalan {getSortIcon('remaining', detailSortConfig)}</th>
                                                <th style={{...styles.th, textAlign: 'center'}}>Aksiyon</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredDetailed.length > 0 ? (
                                                filteredDetailed.map((item, idx) => (
                                                    <tr key={idx} style={styles.tr}>
                                                        <td style={{...styles.td, fontWeight: '600'}}>
                                                            <span 
                                                                style={styles.link} 
                                                                onClick={() => { onClose(); navigate(`/siparisler/${item.orderId}`); }}
                                                            >
                                                                #{item.orderNo}
                                                            </span>
                                                        </td>
                                                        <td style={styles.td}>{item.customer}</td>
                                                        <td style={{...styles.td, fontWeight: '500', color: '#334155'}}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                {item.imageUrl ? (
                                                                    <img src={item.imageUrl} alt={item.name} style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                                                                ) : (
                                                                    <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', border: '1px solid #e2e8f0' }}>📦</div>
                                                                )}
                                                                <span style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.name}>{item.name}</span>
                                                            </div>
                                                        </td>
                                                        <td style={styles.td}>
                                                            <span style={{
                                                                ...styles.badge, 
                                                                background: item.daysWaiting > 7 ? '#fef2f2' : (item.daysWaiting > 3 ? '#fffbeb' : '#f1f5f9'),
                                                                color: item.daysWaiting > 7 ? '#ef4444' : (item.daysWaiting > 3 ? '#f59e0b' : '#64748b'),
                                                                border: `1px solid ${item.daysWaiting > 7 ? '#fecaca' : (item.daysWaiting > 3 ? '#fde68a' : '#e2e8f0')}`
                                                            }}>
                                                                {item.daysWaiting === 0 ? 'Bugün' : `${item.daysWaiting} Gün`}
                                                            </span>
                                                        </td>
                                                        <td style={{...styles.td, textAlign: 'right', fontWeight: '700', color: '#ef4444'}}>
                                                            {item.remaining}
                                                        </td>
                                                        <td style={{...styles.td, textAlign: 'center'}}>
                                                            <button 
                                                                style={styles.actionBtn}
                                                                onClick={() => { onClose(); navigate(`/siparisler/${item.orderId}`); }}
                                                            >
                                                                🚀 Sevkiyat Oluştur
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" style={styles.emptyState}>Detay bulunamadı.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    @keyframes slideIn {
                        from { transform: translateY(20px); opacity: 0; }
                        to { transform: translateY(0); opacity: 1; }
                    }
                `}
            </style>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '24px',
    },
    modal: {
        background: '#ffffff',
        borderRadius: '24px',
        width: '100%',
        maxWidth: '1200px',
        height: '85vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        animation: 'slideIn 0.3s ease-out',
        overflow: 'hidden'
    },
    header: {
        padding: '24px 32px',
        borderBottom: '1px solid #f1f5f9',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        background: 'linear-gradient(to right, #ffffff, #f8fafc)'
    },
    title: {
        margin: 0,
        fontSize: '24px',
        fontWeight: '800',
        color: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    subtitle: {
        margin: '4px 0 0 0',
        fontSize: '14px',
        color: '#64748b'
    },
    closeBtn: {
        background: 'transparent',
        border: 'none',
        fontSize: '28px',
        lineHeight: '1',
        color: '#94a3b8',
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '8px',
        transition: 'all 0.2s',
        ':hover': {
            background: '#f1f5f9',
            color: '#0f172a'
        }
    },
    controls: {
        padding: '20px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f1f5f9',
        flexWrap: 'wrap',
        gap: '16px'
    },
    tabs: {
        display: 'flex',
        gap: '8px',
        background: '#f1f5f9',
        padding: '4px',
        borderRadius: '12px'
    },
    tab: {
        padding: '10px 20px',
        border: 'none',
        background: 'transparent',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    activeTab: {
        background: '#ffffff',
        color: '#0f172a',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    actions: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center'
    },
    searchContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
    },
    searchIcon: {
        position: 'absolute',
        left: '12px',
        color: '#94a3b8',
        fontSize: '14px'
    },
    searchInput: {
        padding: '10px 16px 10px 36px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        fontSize: '14px',
        width: '260px',
        outline: 'none',
        transition: 'all 0.2s',
        ':focus': {
            borderColor: '#06C167',
            boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)'
        }
    },
    exportBtn: {
        padding: '10px 20px',
        background: '#10b981',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)',
        transition: 'all 0.2s',
    },
    content: {
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: '#f8fafc'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '16px'
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '3px solid #e2e8f0',
        borderTopColor: '#06C167',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    loadingText: {
        color: '#64748b',
        fontSize: '15px',
        fontWeight: '500'
    },
    tableWrapper: {
        flex: 1,
        overflow: 'auto',
        padding: '0 32px 32px 32px'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: '0 8px',
    },
    thead: {
        position: 'sticky',
        top: 0,
        background: '#f8fafc',
        zIndex: 1
    },
    th: {
        padding: '16px',
        textAlign: 'left',
        fontSize: '12px',
        fontWeight: '700',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '2px solid #e2e8f0',
        userSelect: 'none'
    },
    tr: {
        background: '#ffffff',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        transition: 'all 0.2s',
        borderRadius: '12px',
    },
    td: {
        padding: '16px',
        fontSize: '14px',
        color: '#475569',
        borderBottom: '1px solid #f1f5f9',
    },
    badge: {
        background: '#f1f5f9',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontFamily: 'monospace',
        color: '#475569',
        border: '1px solid #e2e8f0',
        fontWeight: '600'
    },
    progressContainer: {
        width: '100%',
        height: '6px',
        background: '#f1f5f9',
        borderRadius: '3px',
        overflow: 'hidden',
        marginBottom: '4px'
    },
    progressBar: {
        height: '100%',
        background: '#10b981',
        borderRadius: '3px'
    },
    progressText: {
        fontSize: '11px',
        color: '#64748b',
        textAlign: 'right',
        fontWeight: '600'
    },
    link: {
        color: '#06C167',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'color 0.2s',
        ':hover': {
            color: '#4f46e5',
            textDecoration: 'underline'
        }
    },
    actionBtn: {
        padding: '8px 16px',
        background: '#eff6ff',
        color: '#3b82f6',
        border: '1px solid #bfdbfe',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
        ':hover': {
            background: '#dbeafe'
        }
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px',
        color: '#94a3b8',
        fontSize: '15px',
        background: '#ffffff',
        borderRadius: '12px',
        border: '2px dashed #e2e8f0'
    }
};

export default UnshippedProductsModal;
